import { Box } from '@mui/material';

import { Canvas } from '@react-three/fiber';
import mergeRefs from 'merge-refs';
import { ReviewPacketVerticesData } from 'modules/home/manage-packets/solicitor-viewer/SolicitorDocumentPage';

import React, {
    useCallback,
    useImperativeHandle,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { downloadFile } from 'shared/helpers/DirectDownload';

import { Redacts, inferRedact } from 'shared/shim/react-pdf/PageCanvas';
import Block, { inferBlock } from 'shared/shim/react-pdf/Textures/Block';
import Shapes, { InferShape } from 'shared/shim/react-pdf/Textures/Shapes';
import { Selection } from 'shared/shim/react-pdf/helpers/selectionBox';
import {
    BufferGeometry,
    Material,
    Mesh,
    NormalBufferAttributes,
    MeshStandardMaterial,
    Vector3,
    WebGLRenderer,
} from 'three';

export interface inferImageRedacted extends inferRedact {
    setWidth: (width: any) => void;
    setHeight: (height: any) => void;
    getImageDownload: (filename: string) => void;
}

type PageCanvasProps = {
    _className: string;
    canvasRef?: React.Ref<HTMLCanvasElement>;
    imgInnerRef?: React.Ref<HTMLImageElement>;
    parentStep?: number;
    canDrawn?: boolean;
    src: string;

    verticesData?: ReviewPacketVerticesData[] | null;
    onDrawn?: (startpoint: THREE.Vector3, endpoint: THREE.Vector3) => void;
    onErased?: (index: number) => void;
    onRemove?: (index: number) => void;
    onImageRefresh?: () => void;
    onLoadSuccess?: () => void;
    isScrolling: boolean;
    ref?: React.Ref<inferImageRedacted>;
};

const FibersViewer: React.FC<PageCanvasProps> = React.forwardRef(
    (props, ref) => {
        const {
            canvasRef,
            imgInnerRef,
            canDrawn,
            onDrawn,
            onErased,
            onRemove,
            verticesData,
            _className,
            onImageRefresh,
            onLoadSuccess,
            src,
        } = props;
        const containerViewRef = useRef<HTMLDivElement>(null);
        const imgElement = useRef<HTMLImageElement>(null);

        const canvasWebGLElement = useRef<HTMLCanvasElement>(null);
        const [arrayRedact, setRedacts] = useState<Redacts[]>([]);

        const [isREtexture, setReDrawnTexture] = useState<boolean>(false);
        const innerBlockRef = useRef<Array<inferBlock>>([]);
        const innerShapeRef = useRef<Array<InferShape>>([]);

        const [_originalWidth, setOriginalWidth] = useState<number>(0);
        const [_originalHeight, setOriginalHeight] = useState<number>(0);
        const [width, setWidth] = useState<number>(0);
        const [height, setHeight] = useState<number>(0);

        useImperativeHandle(
            ref,
            () => ({
                setWidth: (width: number) => {
                    setWidth(width);
                    imgElement.current?.setAttribute('width', `${width}`);
                },
                setHeight: (height: number) => {
                    setHeight(height);
                    imgElement.current?.setAttribute('height', `${height}`);
                },
                disposeRenderer: () => {},
                resetRenderer: () => {},
                reinitRenderer: () => {},
                /**
                 * Get current active drawn vertices data
                 * @return {Redacts[]} array of redact object
                 */
                getRedactData: () => arrayRedact,
                /**
                 * Set current active drawn vertices data
                 */
                setRedactData: (data: Redacts[] | null) => {
                    if (data) {
                        setRedacts(data);
                    } else {
                        setRedacts([]);
                    }
                    setReDrawnTexture(true);
                },
                getRenderViewport: () => ({
                    height: _originalHeight,
                    scale: 0,
                    width: _originalWidth,
                }),
                getViewport: () => ({ height: 0, scale: 0, width: 0 }),
                getPageScale: () => 0,
                /**
                 * Set current active drawn vertices data by index
                 */
                setRedactDataByIndex: (data: Redacts | null, index: number) => {
                    if (data) {
                        setRedacts(
                            Array.from(arrayRedact).map((o, i) => {
                                if (i === index) {
                                    return data;
                                }
                                return o;
                            })
                        );
                    } else {
                        setRedacts(arrayRedact.filter((o, i) => i !== index));
                    }
                    setReDrawnTexture(true);
                },
                removeDrawnVertices: () => {
                    setRedacts([]);
                },
                /**
                 * Get total drawn count
                 * @return {number} length of the stored vertices in array
                 */
                getTotalDrawnVertices: () => arrayRedact.length,

                scrollToSpecificRedact: (num: number) => {
                    if (arrayRedact?.some((e, i) => i === num)) {
                        const { current: arrayInnerShape } = innerShapeRef;
                        if (
                            arrayInnerShape &&
                            arrayInnerShape.some((o, i) => i === num)
                        ) {
                            const shape = arrayInnerShape.at(num);
                            shape?.scrollToView();
                        }
                    }
                },
                scrollToSpecificVertices: (num: number) => {
                    if (verticesData?.some((e, i) => i === num)) {
                        const { current: arrayInnerBlock } = innerBlockRef;
                        if (
                            arrayInnerBlock &&
                            arrayInnerBlock.some((o, i) => i === num)
                        ) {
                            const block = arrayInnerBlock.at(num);
                            block?.scrollToView();
                        }
                    }
                },
                getImageDownload: (fileName: string) => {
                    downloadFile(src, fileName);
                },
            }),
            [_originalHeight, _originalWidth, arrayRedact, src, verticesData]
        );
        const handleCanvasRenderer = React.useCallback(
            (canvas: any) =>
                new WebGLRenderer({
                    canvas,

                    alpha: true,
                    preserveDrawingBuffer: true,
                    precision: 'lowp',
                }),
            []
        );

        const blockRef = useRef<THREE.MeshStandardMaterial[]>([]);
        const shapeRef = useRef<THREE.MeshStandardMaterial[]>([]);

        const edgeShapeRef = useRef<Array<THREE.Mesh>>([]);
        const edgeBlockRef = useRef<Array<THREE.Mesh>>([]);
        const groupRef = useRef<THREE.Group | null>(null);
        React.useLayoutEffect(() => {
            const { current: arrayShapes } = shapeRef;
            const { current: arrayBlock } = blockRef;
            const { current: arrayEdgeShapes } = edgeShapeRef;
            const { current: arrayEdgeBlock } = edgeBlockRef;

            arrayShapes.forEach((el) => {
                const texture = el;
                texture.needsUpdate = true;
            });
            arrayBlock.forEach((el) => {
                const texture = el;
                texture.needsUpdate = true;
            });
            arrayEdgeShapes.forEach((el) => {
                const texture = el;
                texture.updateMatrix();
                texture.updateMatrixWorld();
                texture.matrixWorldNeedsUpdate = true;
                texture.updateMorphTargets();
                texture.updateWorldMatrix(true, true);
                // texture.updateMatrix()
            });
            arrayEdgeBlock.forEach((el) => {
                const texture = el;
                texture.updateMatrix();
                texture.updateMatrixWorld();
                texture.matrixWorldNeedsUpdate = true;
                texture.updateMorphTargets();
                texture.updateWorldMatrix(true, true);
                // texture.updateMatrix()
            });
            if (isREtexture) {
                setReDrawnTexture(false);
            }
        }, [isREtexture]);
        const getBlockVerticesColor = React.useCallback(
            (
                active: boolean | undefined,
                corrected: boolean | undefined,
                percentage: string | undefined,
                color: string | undefined
            ) => {
                if (typeof active === 'boolean' && active) {
                    return 'purple';
                }
                if (typeof color === 'string') {
                    return color;
                }
                if (typeof percentage === 'string') {
                    const percentageInteger = parseInt(percentage, 10);
                    if (percentageInteger >= 100) {
                        return 'green';
                    }
                    if (percentageInteger > 50) {
                        return 'red';
                    }
                    if (percentageInteger > 0 && percentageInteger <= 50) {
                        return 'red';
                    }
                    return 'red';
                }
                return 'red';
            },
            []
        );
        /**
         * Handler for erasing drawn box
         * @param {THREE.Vector3} startPoint - start point of selected bounding box
         * @param {THREE.Vector3} endPoint - end point of selected bounding box
         * @param {number} index - ordering number based in array map
         */
        const handleEraseSelectedBox = React.useCallback(
            (
                startPoint: THREE.Vector3,
                endPoint: THREE.Vector3,
                index: number
            ) => {
                if (typeof onErased === 'function') onErased(index);
                setReDrawnTexture(true);
            },
            [onErased]
        );
        /**
         * Handler for erasing saved box
         * @param {THREE.Vector3} startPoint - start point of selected bounding box
         * @param {THREE.Vector3} endPoint - end point of selected bounding box
         * @param {number} index - ordering number based in array map
         */
        const handleEraseSavedVertices = React.useCallback(
            (
                startPoint: THREE.Vector3,
                endPoint: THREE.Vector3,
                index: number,
                indices: number
            ) => {
                const { current: arrayInnerBlock } = innerBlockRef;
                if (
                    arrayInnerBlock &&
                    arrayInnerBlock.some((o, i) => i === index)
                ) {
                    if (typeof onRemove === 'function') onRemove(indices);
                    setReDrawnTexture(true);
                }
            },
            [onRemove]
        );

        /**
         * Handler for selection box when the selectiion is triggered by pointer/mouse listener
         * @param {any} objs - objects from selection
         * @param {THREE.Vector3} startPoint - start point of selected bounding box
         * @param {THREE.Vector3} endPoint - end point of selected bounding box
         */
        const handleSeletionBoxChanged = React.useCallback(
            (objs: any, startPoint: THREE.Vector3, endPoint: THREE.Vector3) => {
                if (
                    endPoint &&
                    startPoint &&
                    (endPoint.x !== startPoint.x ||
                        endPoint.y !== startPoint.y) &&
                    Math.abs(endPoint.x - startPoint.x) <= 0.012 === false &&
                    Math.abs(endPoint.y - startPoint.y) <= 0.012 === false
                ) {
                    setRedacts((prevState: Redacts[]) => [
                        ...prevState,
                        {
                            endPoint: new Vector3().set(
                                endPoint.x,
                                endPoint.y,
                                endPoint.z
                            ),
                            startPoint: new Vector3().set(
                                startPoint.x,
                                startPoint.y,
                                startPoint.z
                            ),
                        },
                    ]);
                    if (typeof onDrawn === 'function') {
                        onDrawn(startPoint, endPoint);
                        // setAlreadyDrawn(true);
                    }
                }
            },
            [onDrawn]
        );
        const [isMetaLoaded, setMetaLoaded] = useState<boolean>(false);
        const handleOnloadedMetadata = useCallback(
            (e: any) => {
                const image: HTMLImageElement = e.currentTarget;
                setOriginalHeight(image.height);
                setOriginalWidth(image.width);
                if (typeof onLoadSuccess === 'function') {
                    onLoadSuccess();
                }
                setMetaLoaded(true);
            },
            [onLoadSuccess]
        );
        useLayoutEffect(() => {
            if (isMetaLoaded) {
                if (typeof onImageRefresh === 'function') {
                    onImageRefresh();
                }
                setMetaLoaded(false);
            }
        }, [isMetaLoaded]);
        useLayoutEffect(() => {
            const { current: img } = imgElement;

            img?.addEventListener('load', handleOnloadedMetadata);
            return () => {
                img?.removeEventListener('load', handleOnloadedMetadata);
            };
        }, []);

        return (
            <div className="overlay-img__Canvas viewer">
                <div ref={containerViewRef} className="twoDimension__images">
                    <img
                        className={`${_className}__img`}
                        dir="ltr"
                        src={src}
                        ref={mergeRefs(imgInnerRef, imgElement)}
                    />
                </div>

                <Box className="threeDimension__Canvas">
                    <Canvas
                        performance={{
                            current: 1,
                            min: 0.1,
                            max: 1,
                            debounce: 800,
                        }}
                        style={{ width, height }}
                        frameloop="always"
                        gl={handleCanvasRenderer}
                        camera={{ position: [0, 0, -1] }}
                        className={`${_className}__canvas_overlay overlay`}
                        dir="ltr"
                        ref={mergeRefs(canvasRef, canvasWebGLElement)}
                    >
                        <>
                            <ambientLight intensity={7} />
                            <group>
                                {verticesData?.map((element, index) => (
                                    <Block
                                        shown
                                        color={getBlockVerticesColor(
                                            element.isActive,
                                            false,
                                            element.percentageMatch,
                                            element.color
                                        )}
                                        canRemove={canDrawn}
                                        edgeRef={(
                                            ref: Mesh<
                                                BufferGeometry<NormalBufferAttributes>,
                                                Material | Material[]
                                            > | null
                                        ) => {
                                            if (ref !== null)
                                                edgeBlockRef.current[index] =
                                                    ref;
                                        }}
                                        textureRef={(
                                            ref: MeshStandardMaterial | null
                                        ) => {
                                            if (ref !== null)
                                                blockRef.current[index] = ref;
                                        }}
                                        ref={(ref: inferBlock | null) => {
                                            if (ref !== null)
                                                innerBlockRef.current[index] =
                                                    ref;
                                        }}
                                        key={index}
                                        startPoint={new Vector3(
                                            element.x1,
                                            element.y1,
                                            1
                                        ).multiplyScalar(
                                            width / _originalWidth
                                        )}
                                        endPoint={new Vector3(
                                            element.x3,
                                            element.y3,
                                            1
                                        ).multiplyScalar(
                                            width / _originalWidth
                                        )}
                                        onRemove={() =>
                                            handleEraseSavedVertices(
                                                new Vector3(
                                                    element.x1,
                                                    element.y1,
                                                    1
                                                ).multiplyScalar(
                                                    width / _originalWidth
                                                ),
                                                new Vector3(
                                                    element.x3,
                                                    element.y3,
                                                    1
                                                ).multiplyScalar(
                                                    width / _originalWidth
                                                ),
                                                index,
                                                index
                                            )
                                        }
                                    />
                                ))}
                            </group>
                            <group
                                ref={groupRef}
                                matrixAutoUpdate={false}
                                matrixWorldAutoUpdate={false}
                            >
                                {arrayRedact.map((element, index) => (
                                    <Shapes
                                        edgeRef={(ref) => {
                                            if (ref !== null)
                                                edgeShapeRef.current[index] =
                                                    ref;
                                        }}
                                        textureRef={(ref) => {
                                            if (ref !== null)
                                                shapeRef.current[index] = ref;
                                        }}
                                        ref={(ref) => {
                                            if (ref !== null)
                                                innerShapeRef.current[index] =
                                                    ref;
                                        }}
                                        key={index}
                                        startPoint={
                                            new Vector3(
                                                element.startPoint.x,
                                                element.startPoint.y,
                                                1
                                            )
                                        }
                                        endPoint={
                                            new Vector3(
                                                element.endPoint.x,
                                                element.endPoint.y,
                                                1
                                            )
                                        }
                                        onErase={() =>
                                            handleEraseSelectedBox(
                                                new Vector3(
                                                    element.startPoint.x,
                                                    element.startPoint.y,
                                                    1
                                                ),
                                                new Vector3(
                                                    element.endPoint.x,
                                                    element.endPoint.y,
                                                    1
                                                ),
                                                index
                                            )
                                        }
                                        // onDragging={handleDraggingFromShapes}
                                    />
                                ))}
                            </group>
                            <group>
                                {canDrawn && (
                                    <Selection
                                        onSelectionChanged={
                                            handleSeletionBoxChanged
                                        }
                                    />
                                )}
                            </group>
                        </>
                    </Canvas>
                </Box>
            </div>
        );
    }
);

export default FibersViewer;
